import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonModal,
    IonText,
} from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { MatchPhone } from "refsix-js-models";
import { setUserActionsCheckList } from "../redux/actions/userActionsCheckList";
import { routes } from "../route/constants";
import { RootState } from "../redux/store";
import {
    getLeaderboardPreferences,
    isEligible,
} from "../services/leaderboardOptInService";

/**
 * The one-time "you are on venue leaderboards" notice (SCORZA-739).
 *
 * This used to be a consent prompt. Boards are on by default now, so it is a notice: it tells the
 * user they are listed and under what name, and points at the setting. It writes nothing — both
 * buttons do the same thing to the profile, which is nothing at all.
 *
 * Keeping it matters. Default-on makes the lawful basis legitimate interests rather than consent,
 * which carries a transparency duty and a visible objection route, and for the users below who
 * were previously excluded, being published with no notice at all would be the worst version of
 * this change.
 *
 * **It fires on the first tracked match while *eligible*, not simply the first tracked match.**
 * That one condition is what covers every way a user becomes listed, with no birthday job and no
 * special cases:
 *
 *   - a new adult — their first tracked match, as before;
 *   - a 17-year-old who turns 18 — their first tracked match after the birthday, because
 *     `isEligible` flips and they have never been shown this;
 *   - a legacy account with no dob who supplies one and turns out to be an adult.
 *
 * "Seen" lives in the user's `userActionsCheckList`, which syncs with their database, so it follows
 * the account rather than the device: switching accounts on one phone, reinstalling, or moving to a
 * new device does not bring it back. Nothing is decided until `matches.loading` goes false:
 * `loadConfigsFromDatabase` dispatches the matches before the checklist, and the render in between
 * would otherwise see a tracked match with an empty checklist and open the notice on every login.
 *
 * The entry for the match that triggered this may already have been written by the time the
 * notice appears — `submitMatchAndTagVenue` runs at the end of the playing-periods flow, and on
 * the first view of an untagged result. That is intentional: this is a notice, not a checkpoint,
 * and nothing is unrecoverable because leaving queues an erase that deletes their rows and
 * rebuilds the affected boards.
 *
 * It is also the only place the invitation lives now. The result page's Local Legend slot used to
 * carry a `notOptedIn` tier for people who had left, but the ribbons are resolved from the
 * `venueRef` the server writes back on a 202 and an opted-out user never gets one — so that tier
 * is unreachable from a result. Opting back in happens in the leaderboards setting this points at.
 */
export const LeaderboardNoticeModal: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const profile = useSelector(
        (state: RootState) => state.currentUser?.profile
    );
    const matches = useSelector((state: RootState) => state.matches.matches);
    const matchesLoaded = useSelector(
        (state: RootState) => state.matches.loading === false
    );
    const noticeSeen = useSelector(
        (state: RootState) =>
            !!state.userActionsCheckList?.leaderboardsNoticeSeen
    );

    const [isOpen, setIsOpen] = useState(false);

    const eligible = isEligible(profile);
    const hasTrackedMatch = (matches || []).some(
        (match: MatchPhone) => match.hasTracking && match.matchFinished
    );
    const displayName = getLeaderboardPreferences(profile).displayName;

    useEffect(() => {
        if (
            matchesLoaded &&
            profile &&
            eligible &&
            hasTrackedMatch &&
            !noticeSeen
        ) {
            setIsOpen(true);
        }
    }, [matchesLoaded, eligible, hasTrackedMatch, noticeSeen, !!profile]);

    const markShownAndClose = () => {
        dispatch(setUserActionsCheckList({ leaderboardsNoticeSeen: true }));
        setIsOpen(false);
    };

    /** Acknowledging lands them on Results rather than wherever the notice caught them: it fires
     *  off the back of finishing a match, so that is the screen the rest of that flow leads to. */
    const handleGotIt = () => {
        markShownAndClose();
        history.push(routes.tabResults);
    };

    const handleOpenSettings = () => {
        markShownAndClose();
        history.push(routes.moreSettings);
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={markShownAndClose}
            breakpoints={[1]}
            initialBreakpoint={1}
            showBackdrop
            data-testid="leaderboards-notice"
        >
            <IonCard className="ion-card-transparent">
                <IonCardHeader>
                    <IonCardTitle>
                        {t("leaderboards.Prompt.title")}
                    </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                    <IonText>
                        <p data-testid="leaderboards-notice-body">
                            {t("leaderboards.Prompt.body", {
                                displayName: displayName,
                            })}
                        </p>
                        <p>{t("leaderboards.Settings.locationWarning")}</p>
                    </IonText>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: 20,
                        }}
                    >
                        <IonButton
                            data-testid="leaderboards-notice-got-it"
                            onClick={handleGotIt}
                        >
                            {t("leaderboards.Prompt.gotIt")}
                        </IonButton>
                        <IonButton
                            data-testid="leaderboards-notice-settings"
                            fill="clear"
                            onClick={handleOpenSettings}
                        >
                            {t("leaderboards.Prompt.openSettings")}
                        </IonButton>
                    </div>
                </IonCardContent>
            </IonCard>
        </IonModal>
    );
};

export default LeaderboardNoticeModal;
